import React, { useEffect, useState } from 'react'
import { Alert, Stack, Typography } from '@mui/material'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import OperationComparisonView from './OperationComparisonView'
import { getOperationUpdateImpact, getOperationVersionDetail, readOperationLibraryError } from './operationLibraryApi'
import { planOperationUpdate } from './feature/featureGroupRevisionControl'

/**
 * OperationUpdateImpactDialog (C6, §8.7) — qué cambiaría al actualizar este nodo a la versión
 * candidata, y si el guardián D15 lo permite (CA5.2/CA5.3, S91).
 *
 * Lee `GET .../operationUpdateImpact/{targetNodeId}` (N14) y PINTA lo que recibe: `comparison` por
 * bloques, `allowed`, `jobHasBeenExecuted` y `blockingElements`. **Jupiter no calcula el veredicto**
 * — lo decide Sun, que es quien sabe si el job ya se ejecutó.
 *
 * Ajuste del PO (2026-09-16): `SmartDialog` con `SmartMateriaUITable` dentro, tanto para los cambios
 * (a través de `OperationComparisonView`) como para lo que bloquea la actualización.
 */
const OperationUpdateImpactDialog = ({
  open = false,
  workflowId = 0,
  nodeId = '',
  targetNodeId = '',
  featureGroupRevisionControlled = false,
  jobReference = {},
  featureGroupBaselines = {},
  onConfirm = () => {},
  onClose = () => {}
}) => {
  const { t_ } = useSmartTranslation()
  const [impact, setImpact] = useState(null)
  const [error, setError] = useState('')
  // Story 186 (D-25): the job of the target version, read only in CONTROLLED mode to announce the approved groups it changes
  const [operationJob, setOperationJob] = useState({})

  useEffect(() => {
    let current = true
    if (!open || !workflowId || !nodeId || !targetNodeId) {
      setImpact(null)
      return () => { current = false }
    }
    setError('')
    getOperationUpdateImpact(workflowId, nodeId, targetNodeId)
      .then((result) => { if (current) setImpact(result || null) })
      .catch((thrown) => { if (current) setError(readOperationLibraryError(thrown).error) })
    return () => { current = false }
  }, [open, workflowId, nodeId, targetNodeId])

  useEffect(() => {
    let current = true
    if (!open || !featureGroupRevisionControlled || !targetNodeId) {
      // Nothing to reset in FREE mode: no state change, no extra render
      setOperationJob((previous) => Object.keys(previous).length > 0 ? {} : previous)
      return () => { current = false }
    }
    getOperationVersionDetail(targetNodeId)
      .then((detail) => { if (current) setOperationJob(detail?.jobReference || {}) })
      // Without the content of the version nothing can be announced: the server applies the rule anyway (RN51)
      .catch(() => { if (current) setOperationJob({}) })
    return () => { current = false }
  }, [open, featureGroupRevisionControlled, targetNodeId])

  const allowed = impact?.allowed === true
  const { newVersions, removed } = featureGroupRevisionControlled
    ? planOperationUpdate({ nodeJob: jobReference, operationJob, baselines: featureGroupBaselines })
    : { newVersions: [], removed: [] }

  return (
    <SmartDialog
      setOpen={open}
      title={{ render: <Typography variant='h6'>{t_('Update')}</Typography> }}
      acceptLabel={t_('Update')}
      acceptCallback={() => onConfirm(targetNodeId)}
      // Solo el ACEPTAR depende del veredicto: cancelar siempre tiene que poder pulsarse.
      disableAccept={!allowed}
      cancelCallback={onClose}
      autoClose={false}
      renderComponent={
        <Stack spacing={2} sx={{ mt: 1, minWidth: 360 }} data-testid='operation-update-impact-dialog'>
          {error ? <Alert severity='error' data-testid='operation-update-impact-error'>{t_(error)}</Alert> : null}

          {/* Historia 152 (S40/S43 · RN-01.23, RN-01.23d): adoptar SUSTITUYE el contenido entero, no
              fusiona. Lo ÚNICO que cambia entre un nodo `SYNCHRONIZED` y uno `CUSTOMIZED` es este
              aviso, y quien decide si hay algo que perder es Sun (`customizationsWillBeLost`, N14):
              el cliente no lo recalcula. */}
          {impact?.customizationsWillBeLost
            ? (
              <Alert severity='warning' data-testid='operation-update-customizations-lost'>
                {t_('Adopting replaces all the content: your customizations will be lost')}
              </Alert>
              )
            : null}

          {/* Story 186 (S161, D-25): the approved feature groups that the update turns into a new DRAFT version (S161) and those it removes (S170, E2-7) */}
          {newVersions.length + removed.length > 0
            ? (
              <Alert severity='warning' data-testid='operation-update-revision-notice'>
                <Typography variant='body2'>{t_('Updating the operation changes approved feature groups:')}</Typography>
                <ul>
                  {newVersions.map((newVersion, index) => (
                    <li key={'version-' + index}>{t_('{{code}} → v{{version}}', newVersion)}</li>
                  ))}
                  {removed.map((item, index) => (
                    <li key={'removed-' + index}>{t_('{{code}}: will be removed', item)}</li>
                  ))}
                </ul>
              </Alert>
              )
            : null}

          {impact?.jobHasBeenExecuted
            ? (
              <Alert severity='warning' data-testid='operation-update-executed'>
                {t_('This job has already been executed')}
              </Alert>
              )
            : null}

          {!allowed && impact
            ? (
              <Stack spacing={1} data-testid='operation-update-blocking'>
                <Typography variant='subtitle2'>{t_('Blocking changes')}</Typography>
                <SmartMateriaUITable
                  dense
                  sticky={false}
                  searchAndPaginationVisible='none'
                  tablePageSize={Number.MAX_SAFE_INTEGER}
                  primaryKeys={['id']}
                  sortOptions={{ disableAutoSort: true }}
                  columns={[
                    { name: t_('Name'), field: 'label', readOnly: true },
                    { name: t_('Value'), field: 'detail', readOnly: true, render: (row) => (row.detail || '—') }
                  ]}
                  dataFetch={() => Promise.resolve((impact.blockingElements || []).map((element, index) => ({
                    ...element, id: element.key + '#' + index
                  })))}
                />
              </Stack>
              )
            : null}

          <OperationComparisonView comparison={impact?.comparison} />
        </Stack>
      }
    />
  )
}

export default OperationUpdateImpactDialog
